import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { SERVICES, INDUSTRIES } from "../data/content";

export const ServicesGrid = ({ limit }) => {
  const navigate = useNavigate();
  const list = limit ? SERVICES.slice(0, limit) : SERVICES;
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {list.map((s, i) => (
        <Reveal key={s.slug} delay={(i % 3) * 0.08}>
          <div
            data-testid={`service-card-${s.slug}`}
            onClick={() => navigate(`/services#${s.slug}`)}
            className={`group flex h-full cursor-pointer flex-col rounded-3xl border p-7 sm:p-8 transition-all duration-500 hover:-translate-y-2 ${
              s.premium
                ? "border-dg-cyan/30 bg-gradient-to-br from-dg-blue/20 to-dg-navy2 hover:border-dg-cyan/60 hover:shadow-[0_0_40px_rgba(0,184,212,0.2)]"
                : "border-white/8 bg-white/[0.03] hover:border-dg-cyan/40 hover:bg-white/[0.05] hover:shadow-[0_0_30px_rgba(0,184,212,0.1)]"
            }`}
          >
            <div className="flex items-start justify-between">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white shadow-lg shadow-dg-blue/30 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6">
                <s.icon size={21} />
              </span>
              {s.premium && (
                <span className="rounded-full border border-dg-cyan/40 bg-dg-cyan/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-dg-cyan">
                  Premium
                </span>
              )}
            </div>
            <h3 className="font-display mt-6 text-lg sm:text-xl font-bold text-white">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-400">{s.tagline}</p>
            <ul className="mt-5 flex-1 space-y-1.5">
              {s.items.slice(0, 4).map((it) => (
                <li key={it} className="flex items-center gap-2 text-[13px] text-slate-400">
                  <span className="h-1 w-1 rounded-full bg-dg-cyan" /> {it}
                </li>
              ))}
            </ul>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-dg-cyan">
              Explore <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </span>
          </div>
        </Reveal>
      ))}
    </div>
  );
};

export const IndustriesGrid = ({ compact = false }) => (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
    {INDUSTRIES.map((ind, i) => (
      <Reveal key={ind.name} delay={(i % 4) * 0.06}>
        <div
          data-testid={`industry-card-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
          className="group h-full rounded-2xl border border-dg-blue/10 bg-white p-5 sm:p-6 shadow-lg shadow-blue-900/5 transition-all duration-500 hover:-translate-y-1.5 hover:border-dg-cyan/50 hover:shadow-xl hover:shadow-dg-blue/10"
        >
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-dg-light text-dg-blue transition-all duration-500 group-hover:bg-dg-navy group-hover:text-dg-cyan">
            <ind.icon size={19} />
          </span>
          <h3 className="font-display mt-4 text-sm sm:text-base font-bold text-dg-navy">{ind.name}</h3>
          {!compact && <p className="mt-1.5 text-xs sm:text-[13px] leading-relaxed text-slate-500">{ind.desc}</p>}
        </div>
      </Reveal>
    ))}
  </div>
);

export const ServicesSectionHead = () => (
  <SectionHead
    eyebrow="What We Do"
    title="Our Digital Services"
    sub="From your first idea to long-term digital growth, we provide complete solutions under one roof."
  />
);
